<template>
  <v-expansion-panels v-model="painelAberto" flat accordion class="c-anexos">
    <v-expansion-panel>
      <v-expansion-panel-header class="c-anexos__header">
        <div class="d-flex align-center flex-wrap">
          <v-icon color="#5c5c5c" class="mr-2">mdi-paperclip</v-icon>
          <span class="c-anexos__title">{{ titulo }}</span>
          <v-chip
            v-if="arquivosPersistidos.length > 0"
            small
            outlined
            color="#5c5c5c"
            class="ml-3 mt-1 mb-1"
          >
            {{ arquivosPersistidos.length }} salvos
          </v-chip>
          <v-chip
            v-if="arquivosPendentes.length > 0"
            small
            color="#363494"
            text-color="white"
            class="ml-2 mt-1 mb-1"
          >
            {{ arquivosPendentes.length }} pendentes
          </v-chip>
        </div>
      </v-expansion-panel-header>

      <v-expansion-panel-content>
        <v-alert
          v-if="mensagemContexto"
          dense
          outlined
          type="info"
          class="mb-4"
        >
          {{ mensagemContexto }}
        </v-alert>

        <v-file-input
          v-model="selecaoArquivos"
          multiple
          small-chips
          counter
          show-size
          outlined
          dense
          prepend-icon="mdi-paperclip"
          :accept="accept"
          label="Selecionar documentos"
          @change="adicionarArquivosSelecionados"
        />

        <div
          v-if="arquivosPendentes.length > 0"
          class="mb-4"
        >
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="c-anexos__subtitle">Fila de envio</span>
            <div class="d-flex align-center">
              <v-btn
                v-if="modoEfetivo === 'persistido'"
                small
                color="#363494"
                dark
                class="mr-2"
                :loading="uploading"
                @click="enviarArquivosPendentes"
              >
                Enviar anexos
              </v-btn>
              <v-btn
                small
                text
                color="#75756d"
                @click="limparFila"
              >
                Limpar fila
              </v-btn>
            </div>
          </div>

          <v-list dense class="c-anexos__list">
            <v-list-item
              v-for="(arquivo, index) in arquivosPendentes"
              :key="`${arquivo.name}-${arquivo.lastModified}-${index}`"
            >
              <v-list-item-content>
                <v-list-item-title>{{ arquivo.name }}</v-list-item-title>
                <v-list-item-subtitle>{{ formatarTamanho(arquivo.size) }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn icon small @click="removerDaFila(index)">
                  <v-icon small color="#a94442">mdi-close</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <div v-if="modoEfetivo === 'fila_local'" class="text-caption grey--text text--darken-1 mt-2">
            Os anexos serao enviados automaticamente depois do salvamento.
          </div>
        </div>

        <v-alert
          v-if="resultadoUpload.failed.length > 0"
          dense
          outlined
          type="warning"
          class="mb-4"
        >
          <div class="font-weight-medium mb-2">
            Registro salvo, mas alguns anexos nao foram enviados.
          </div>
          <div
            v-for="(item, index) in resultadoUpload.failed"
            :key="`${item.nomeOriginal}-${index}`"
            class="text-caption"
          >
            {{ item.nomeOriginal }}: {{ item.motivo }}
          </div>
        </v-alert>

        <v-alert
          v-else-if="resultadoUpload.uploaded.length > 0"
          dense
          outlined
          type="success"
          class="mb-4"
        >
          {{ resultadoUpload.uploaded.length }} anexo(s) enviado(s) com sucesso.
        </v-alert>

        <div v-if="modoEfetivo === 'persistido'" class="c-anexos__persistidos">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="c-anexos__subtitle">Arquivos salvos</span>
            <v-progress-circular
              v-if="loadingArquivos"
              indeterminate
              size="18"
              width="2"
              color="#363494"
            />
          </div>

          <v-list v-if="arquivosPersistidos.length > 0" dense class="c-anexos__list">
            <v-list-item
              v-for="arquivo in arquivosPersistidos"
              :key="arquivo.id"
            >
              <v-list-item-content>
                <v-list-item-title>{{ arquivo.nomeOriginal }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ formatarTamanho(arquivo.tamanhoBytes) }}
                  <span v-if="arquivo.createdAt"> | {{ formatarData(arquivo.createdAt) }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action class="c-anexos__actions">
                <v-btn
                  icon
                  small
                  :loading="downloadId === arquivo.id"
                  @click="baixarArquivo(arquivo)"
                >
                  <v-icon small color="#363494">mdi-download</v-icon>
                </v-btn>

                <v-btn
                  icon
                  small
                  :loading="deleteId === arquivo.id"
                  @click="excluirArquivo(arquivo)"
                >
                  <v-icon small color="#a94442">mdi-delete</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <div
            v-else-if="!loadingArquivos"
            class="text-caption grey--text text--darken-1"
          >
            Nenhum documento salvo para este registro.
          </div>
        </div>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import axios from "axios";
import { urlApi, showError } from "@/global";
import { formatarDataParaExibicao } from "@/utils/date";

const ACCEPTED_EXTENSIONS = [
  "pdf",
  "png",
  "jpg",
  "jpeg",
  "webp",
  "doc",
  "docx",
  "xls",
  "xlsx",
];
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;

const countByName = (items, field = "nomeOriginal") =>
  (items || []).reduce((acc, item) => {
    const key = item && item[field];
    if (!key) return acc;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

const consumeCount = (counter, key) => {
  if (!counter[key]) return false;
  counter[key] -= 1;
  return true;
};

export default {
  name: "gerenciadorAnexos",
  props: {
    tipoEntidade: {
      type: String,
      required: true,
    },
    entidadeId: {
      type: [Number, String],
      default: null,
    },
    modo: {
      type: String,
      default: "fila_local",
    },
    titulo: {
      type: String,
      default: "Documentos complementares",
    },
    abertoInicial: {
      type: Boolean,
      default: false,
    },
    mensagemOpcional: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      painelAberto: this.abertoInicial ? 0 : null,
      modoInterno: this.modo,
      entidadeIdInterna: this.entidadeId || null,
      selecaoArquivos: null,
      arquivosPendentes: [],
      arquivosPersistidos: [],
      resultadoUpload: {
        uploaded: [],
        failed: [],
      },
      loadingArquivos: false,
      uploading: false,
      deleteId: null,
      downloadId: null,
    };
  },
  computed: {
    accept() {
      return ACCEPTED_EXTENSIONS.map((ext) => `.${ext}`).join(",");
    },
    currentEntityId() {
      return this.entidadeId || this.entidadeIdInterna;
    },
    endpointBase() {
      if (!this.currentEntityId) return "";
      return `${urlApi}/${this.tipoEntidade}/${this.currentEntityId}/arquivos`;
    },
    modoEfetivo() {
      return this.modoInterno;
    },
    mensagemContexto() {
      if (this.mensagemOpcional) return this.mensagemOpcional;

      if (this.modoEfetivo === "fila_local") {
        return "Os anexos sao opcionais e nao interrompem o cadastro principal.";
      }

      return "Use esta area para baixar, excluir ou adicionar documentos relacionados ao registro.";
    },
  },
  watch: {
    entidadeId: {
      immediate: true,
      handler(valor) {
        this.entidadeIdInterna = valor || this.entidadeIdInterna;

        if (this.modoEfetivo === "persistido" && this.currentEntityId) {
          this.carregarArquivos();
        }
      },
    },
    modo: {
      immediate: true,
      handler(valor) {
        this.modoInterno = valor;

        if (valor === "persistido" && this.currentEntityId) {
          this.carregarArquivos();
        }
      },
    },
  },
  methods: {
    abrirPainel() {
      this.painelAberto = 0;
    },
    resetState() {
      this.painelAberto = this.abertoInicial ? 0 : null;
      this.modoInterno = this.modo;
      this.entidadeIdInterna = this.entidadeId || null;
      this.selecaoArquivos = null;
      this.arquivosPendentes = [];
      this.arquivosPersistidos = [];
      this.resultadoUpload = { uploaded: [], failed: [] };
    },
    setPersistedContext(entidadeId) {
      this.entidadeIdInterna = entidadeId;
      this.modoInterno = "persistido";
      this.abrirPainel();
      return this.carregarArquivos();
    },
    hasPendingFiles() {
      return this.arquivosPendentes.length > 0;
    },
    getPendingFiles() {
      return [...this.arquivosPendentes];
    },
    emitirFilaAtualizada() {
      this.$emit("fila-alterada", this.getPendingFiles());
    },
    validarArquivoLocal(file) {
      if (!file || !file.name) return "Arquivo invalido.";

      const extension = file.name.split(".").pop().toLowerCase();
      if (!ACCEPTED_EXTENSIONS.includes(extension)) {
        return "Tipo de arquivo nao permitido. Use PDF, imagem ou documento Office.";
      }

      if ((file.size || 0) > MAX_FILE_SIZE_BYTES) {
        return "Cada arquivo deve ter no maximo 20 MB.";
      }

      return null;
    },
    adicionarArquivosSelecionados(files) {
      const selecionados = Array.isArray(files)
        ? files
        : Array.from(files || []);

      if (!selecionados.length) {
        this.selecaoArquivos = null;
        return;
      }

      const validos = [];
      const invalidos = [];

      selecionados.forEach((file) => {
        const mensagemErro = this.validarArquivoLocal(file);
        if (mensagemErro) {
          invalidos.push(`${file.name}: ${mensagemErro}`);
          return;
        }
        validos.push(file);
      });

      if (invalidos.length) {
        this.$toasted.global.defaultError({
          msg: invalidos.join(" | "),
        });
      }

      if (validos.length) {
        this.arquivosPendentes = [...this.arquivosPendentes, ...validos];
        this.resultadoUpload = { uploaded: [], failed: [] };
        this.abrirPainel();
        this.emitirFilaAtualizada();
      }

      this.selecaoArquivos = null;
    },
    removerDaFila(index) {
      this.arquivosPendentes.splice(index, 1);
      this.emitirFilaAtualizada();
    },
    limparFila() {
      this.arquivosPendentes = [];
      this.emitirFilaAtualizada();
    },
    formatarTamanho(bytes) {
      const valor = Number(bytes) || 0;

      if (valor >= 1024 * 1024) {
        return `${(valor / (1024 * 1024)).toFixed(2)} MB`;
      }

      if (valor >= 1024) {
        return `${(valor / 1024).toFixed(1)} KB`;
      }

      return `${valor} B`;
    },
    formatarData(valor) {
      return formatarDataParaExibicao(valor) || "-";
    },
    async carregarArquivos() {
      if (!this.currentEntityId) {
        this.arquivosPersistidos = [];
        return;
      }

      this.loadingArquivos = true;
      try {
        const res = await axios.get(this.endpointBase);
        this.arquivosPersistidos = res.data || [];
      } catch (err) {
        this.arquivosPersistidos = [];
        showError(err);
      } finally {
        this.loadingArquivos = false;
      }
    },
    async uploadPendingFiles(options = {}) {
      const entidadeId = options.entidadeId || this.currentEntityId;
      const silentSuccess = !!options.silentSuccess;

      if (!entidadeId) {
        throw new Error("Registro ainda nao foi criado.");
      }

      if (!this.arquivosPendentes.length) {
        this.resultadoUpload = { uploaded: [], failed: [] };
        return this.resultadoUpload;
      }

      this.uploading = true;

      try {
        if (entidadeId !== this.currentEntityId || this.modoEfetivo !== "persistido") {
          this.entidadeIdInterna = entidadeId;
          this.modoInterno = "persistido";
        }

        const filaAtual = [...this.arquivosPendentes];
        const formData = new FormData();
        filaAtual.forEach((arquivo) => {
          formData.append("arquivos", arquivo, arquivo.name);
        });

        const res = await axios.post(
          `${urlApi}/${this.tipoEntidade}/${entidadeId}/arquivos`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        const resultado = res.data || { uploaded: [], failed: [] };
        this.resultadoUpload = {
          uploaded: resultado.uploaded || [],
          failed: resultado.failed || [],
        };

        const successCounter = countByName(this.resultadoUpload.uploaded);
        const failedCounter = countByName(this.resultadoUpload.failed);

        this.arquivosPendentes = filaAtual.filter((arquivo) => {
          if (consumeCount(successCounter, arquivo.name)) return false;
          if (consumeCount(failedCounter, arquivo.name)) return true;
          return true;
        });

        this.emitirFilaAtualizada();
        await this.carregarArquivos();

        if (!silentSuccess && this.resultadoUpload.failed.length === 0) {
          this.$toasted.global.defaultSuccess({
            msg: "Anexos enviados com sucesso.",
          });
        }

        if (this.resultadoUpload.failed.length > 0) {
          this.abrirPainel();
        }

        this.$emit("upload-completo", this.resultadoUpload);
        return this.resultadoUpload;
      } catch (err) {
        this.abrirPainel();
        showError(err);
        throw err;
      } finally {
        this.uploading = false;
      }
    },
    async enviarArquivosPendentes() {
      try {
        const resultado = await this.uploadPendingFiles();
        if (resultado.failed.length > 0) {
          this.$toasted.global.defaultError({
            msg: "Alguns anexos nao puderam ser enviados. Revise a fila e tente novamente.",
          });
        }
      } catch (err) {
        if (!err || !err.response) {
          this.$toasted.global.defaultError({
            msg: err && err.message ? err.message : "Falha ao enviar anexos.",
          });
        }
      }
    },
    async baixarArquivo(arquivo) {
      this.downloadId = arquivo.id;
      try {
        const res = await axios.get(
          `${this.endpointBase}/${arquivo.id}/download`,
          { responseType: "blob" }
        );
        const blob = new Blob([res.data], {
          type: arquivo.contentType || "application/octet-stream",
        });
        const downloadUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = downloadUrl;
        anchor.download = arquivo.nomeOriginal || "documento";
        document.body.appendChild(anchor);
        anchor.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(anchor);
      } catch (err) {
        showError(err);
      } finally {
        this.downloadId = null;
      }
    },
    async excluirArquivo(arquivo) {
      this.deleteId = arquivo.id;
      try {
        await axios.delete(`${this.endpointBase}/${arquivo.id}`);
        this.arquivosPersistidos = this.arquivosPersistidos.filter((item) => item.id !== arquivo.id);
        this.$emit("arquivo-excluido", arquivo);
        this.$toasted.global.defaultSuccess({
          msg: "Anexo excluido com sucesso.",
        });
      } catch (err) {
        showError(err);
      } finally {
        this.deleteId = null;
      }
    },
  },
};
</script>

<style scoped>
.c-anexos {
  width: 100%;
}

.c-anexos__header {
  padding-left: 0;
  padding-right: 0;
}

.c-anexos__title {
  font-weight: 600;
  color: #4b4b4b;
}

.c-anexos__subtitle {
  font-size: 0.95rem;
  font-weight: 600;
  color: #5c5c5c;
}

.c-anexos__list {
  border: 1px solid #ececec;
  border-radius: 8px;
  background: #fbfbfb;
}

.c-anexos__persistidos {
  margin-top: 8px;
}

.c-anexos__actions {
  flex-direction: row;
}
</style>
